import { useEffect, useRef, useState } from "react";
import { useAuthentication } from "@iad-os/react-ghost-auth";
import { useConfigStore } from "../store/configStore.js";

// Stato del job di scodamento WhatsApp ricevuto in push (SSE) dal backend.
// Permette di abilitare/disabilitare il pulsante "Lancia JOB" in tempo reale,
// senza polling. L'auth usa il bearer token: lo stream viene consumato via
// fetch (EventSource non può inviare header Authorization).

export interface DispatchResult {
  processed?: number;
  sent?: number;
  failed?: number;
  blocked?: number;
  waitingWindow?: number;
  testMode?: boolean;
}

export interface DispatchJob {
  running: boolean;
  startedAt?: string;
  finishedAt?: string;
  lastResult?: DispatchResult;
}

const STATUS_PATH = "/api/v1/whatsapp/queues/dispatch/status";

/** Si collega al canale SSE dello stato job. `onFinished` scatta a ogni transizione running→idle. */
export function useDispatchJob(onFinished?: (result?: DispatchResult) => void): DispatchJob {
  const config = useConfigStore((s) => s.config);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { token } = useAuthentication() as any;
  const [job, setJob] = useState<DispatchJob>({ running: false });
  const wasRunning = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    const baseUrl = config?.metadata.endpoint_api ?? "";
    const accessToken: string | undefined = token?.access_token;
    if (!baseUrl || !accessToken) return;

    const ctrl = new AbortController();
    let stopped = false;

    const connect = async () => {
      while (!stopped) {
        try {
          const res = await fetch(`${baseUrl}${STATUS_PATH}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: ctrl.signal
          });
          if (!res.ok || !res.body) throw new Error(`status ${res.status}`);
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!stopped) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let sep: number;
            while ((sep = buffer.indexOf("\n\n")) >= 0) {
              const frame = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
              if (!dataLine) continue; // heartbeat ": ping"
              try {
                const state: DispatchJob = JSON.parse(dataLine.slice(5).trim());
                setJob(state);
                if (wasRunning.current && !state.running) onFinishedRef.current?.(state.lastResult);
                wasRunning.current = state.running;
              } catch {
                /* frame non valido: ignora */
              }
            }
          }
        } catch {
          if (stopped || ctrl.signal.aborted) return;
        }
        // Connessione caduta: riprova dopo una breve pausa.
        await new Promise((r) => setTimeout(r, 3000));
      }
    };

    void connect();
    return () => {
      stopped = true;
      ctrl.abort();
    };
  }, [config, token]);

  return job;
}
